/* eslint-disable max-lines */
import { Location } from "@angular/common";
import {
    Route,
    Routes,
} from "@angular/router";
import { iconsResolver } from "@Front-end/seed/modules/icons";
import { provideSeedAchievements } from "@Front-end/seed/modules/v2/achievements";
import {
    lotteriesResolver,
    provideSeedLotteries,
} from "@Front-end/seed/modules/v2/lottery";
import { missionsResolver } from "@Front-end/seed/modules/v2/missions";
import {
    LocalizeRouterSettings,
    ManualParserLoader,
} from "@gilsdav/ngx-translate-router";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../../../environments/environment";
import {
    FeatureFlags,
    hasFeatureFlag,
} from "../../constants/feature-flags.constants";
import {
    GENERATED_CATEGORY_PATHS,
    GENERATED_PROVIDERS_PATHS,
} from "../../generated/prerender-routes.generated";
import { AuthUserGuard } from "../../guards/auth-user.guard";
import { DevModeGuard } from "../../guards/dev-mode.guard";

export function createTranslateLoader(
    translate: TranslateService,
    location: Location,
    settings: LocalizeRouterSettings,
): ManualParserLoader {
    return new ManualParserLoader(
        translate,
        location,
        settings,
        environment.local,
        "ROUTES.",
    );
}


// --- LOCALES ---

const LOCALES: string[] = environment.local || ["en"];


// --- HELPERS ---

function generateLocalizedRoutes(routes: Routes, parent?: string): Routes {
    return LOCALES.flatMap((lang: string): Routes =>
        routes.map((route: Route): Route => {
            const localizedPath: string = parent
                ? route.path
                    ? `${lang}/${parent}/${route.path}`
                    : `${lang}/${parent}`
                : route.path
                    ? `${lang}/${route.path}`
                    : lang;

            return {
                ...route,
                path: localizedPath,
                data: {
                    ...route.data,
                    skipRouteLocalization: true,
                },
            };
        }),
    );
}

function withLocalized(routes: Routes, parent?: string): Routes {
    return [
        ...routes,
        ...generateLocalizedRoutes(routes, parent),
    ];
}

function buildGeneratedGameProviderRoutes(paths: string[]): Routes {
    return paths.map((routePath: string): Route => ({
        path: routePath,
        loadChildren: () => import("../../features/game-hall/game-hall-routing").then(m => m.gameHallRoutes),
        data: {
            skipRouteLocalization: true,
        },
    }));
}

function buildGeneratedGameCategoryRoutes(paths: string[]): Routes {
    return paths.map((routePath: string): Route => ({
        path: routePath,
        loadChildren: () => import("../../features/game-hall/game-hall-routing").then(m => m.gameHallRoutes),
        data: {
            skipRouteLocalization: true,
        },
    }));
}


// --- FEATURE ROUTES ---

const devRoutesBase: Routes = environment.domain !== "PROD"
    ? [{
        path: "ui-kit",
        loadChildren: () => import("../../modules/ui-kit/ui-kit-routing").then(m => m.uiKitRoutes),
        canLoad: [DevModeGuard],
        data: {
            hideHeader: true,
            hideFooter: true,
            hideArguments: true,
            hideBottomMenu: true,
            hideBottomBanner: true,
        },
    }]
    : [];

const devRoutes: Routes = withLocalized(devRoutesBase);

const tournamentRoutesBase: Routes = hasFeatureFlag(FeatureFlags.TOURNAMENTS_URL)
    ? [
        {
            path: "tournaments",
            data: {
                sitemap: { loc: "/tournaments" },
            },
            loadChildren: () => import("../../features/tournaments/tournaments-routing").then(m => m.tournamentsRoutes),
        },
        {
            path: "tournaments/:id",
            data: {},
            loadChildren: () => import("../../features/tournaments/tournament-item-routing").then(m => m.tournamentItemRouting),
        },
    ]
    : [];

const tournamentRoutes: Routes = withLocalized(tournamentRoutesBase);

const achievementsRoutesBase: Routes = hasFeatureFlag(FeatureFlags.ACHIEVEMENTS_URL)
    ? [
        {
            path: "achievements",
            data: {
                sitemap: { loc: "/achievements" },
            },
            providers: [
                provideSeedAchievements(),
            ],
            loadComponent: () => import("../../features/achievements/achievements.component").then(c => c.AchievementsComponent),
        },
    ]
    : [];

const achievementsRoutes: Routes = withLocalized(achievementsRoutesBase);

const missionsRoutesBase: Routes = hasFeatureFlag(FeatureFlags.MISSIONS_URL)
    ? [
        {
            path: "missions",
            data: {
                sitemap: { loc: "/missions" },
            },
            resolve: {
                missions: missionsResolver,
            },
            loadChildren: () => import("../../features/missions/missions-routing").then(m => m.missionsRoutes),
        },
        {
            path: "missions/:alias",
            data: {},
            loadChildren: () => import("../../features/missions/missions-category-routing").then(m => m.missionsCategoryRouting),
        },
    ]
    : [];

const missionsRoutes: Routes = withLocalized(missionsRoutesBase);

const lotteriesRoutesBase: Routes = hasFeatureFlag(FeatureFlags.LOTTERIES_URL)
    ? [
        {
            path: "lotteries",
            data: {
                sitemap: { loc: "/lotteries" },
            },
            providers: [
                provideSeedLotteries(),
            ],
            resolve: {
                lotteries: lotteriesResolver,
            },
            loadChildren: () => import("../../features/lotteries/lotteries-routing").then(m => m.lotteriesRoutes),
        },
    ]
    : [];

const lotteriesRoutes: Routes = withLocalized(lotteriesRoutesBase);

const sportRoutesBase: Routes = hasFeatureFlag(FeatureFlags.SPORT)
    ? [
        {
            path: "sport",
            data: {
                hideHeaderPromo: true,
                hideBottomBanner: true,
                sitemap: {
                    loc: "/sport",
                    priority: "0.8",
                    changefreq: "weekly",
                },
            },
            loadChildren: () => import("../../features/sport/sport-routing").then(m => m.sportRoutes),
        },
    ]
    : [];

const sportRoutes: Routes = withLocalized(sportRoutesBase);

const generatedGameProviderRoutes: Routes = buildGeneratedGameProviderRoutes(GENERATED_PROVIDERS_PATHS);
const localizedGameProviderRoutes: Routes = withLocalized(generatedGameProviderRoutes);

const generatedGameCategoryRoutes: Routes = buildGeneratedGameCategoryRoutes(GENERATED_CATEGORY_PATHS);
const localizedGameCategoryRoutes: Routes = withLocalized(generatedGameCategoryRoutes);

// --- MAIN ROUTES ---

const mainRoute: Routes = [
    {
        path: "",
        data: {
            sitemap: {
                loc: "/",
                priority: "1.0",
            },
        },
        resolve: {
            footerIcons: iconsResolver,
        },
        loadChildren: () => import("../../features/home/home-routing").then(m => m.homeRoutes),
    },
    {
        path: "games",
        loadChildren: () => import("../../features/game-hall/game-hall-routing").then(m => m.gameHallRoutes),
    },
    {
        path: "shop",
        data: {
            sitemap: {
                loc: "/shop",
                priority: "0.8",
                changefreq: "weekly",
            },
        },
        loadChildren: () => import("../../features/shop/shop-routing").then(m => m.shopRoutes),
    },
    {
        path: "promotions",
        data: {
            sitemap: {
                loc: "/promotions",
                priority: "0.8",
                changefreq: "weekly",
            },
        },
        loadChildren: () => import("../../features/promotions/promo-routing").then(m => m.promoRoutes),
    },
    {
        path: "promotions/:name",
        loadChildren: () => import("../../features/promotions/promo-item-routing").then(m => m.promoItemRouting),
    },
    {
        path: "info",
        data: {
            sitemap: { loc: "/info" },
        },
        loadChildren: () => import("../../features/info-pages/info-pages-routing").then(m => m.InfoPageRoutes),
    },
    {
        path: "vip",
        data: {
            sitemap: {
                loc: "/vip",
                priority: "0.8",
                changefreq: "weekly",
            },
        },
        loadChildren: () => import("../../features/vip/vip-routing").then(m => m.vipRoutes),
    },
    {
        path: "profile",
        data: {
            sitemap: { loc: "/profile" },
            isProfilePage: true,
        },
        loadChildren: () => import("../../features/account-profile/account-profile-routing").then(m => m.AccountProfileRoutes),
        canLoad: [AuthUserGuard],
    },
    {
        path: "not-found",
        loadComponent: () => import("../../components/not-found/not-found.component").then(c => c.NotFoundComponent),
    },
    {
        path: "game",
        data: {
            hideHeader: true,
            hideFooter: true,
            hideMenu: true,
            hideBottomMenu: true,
            hideBottomBanner: true,
        },
        loadChildren: () => import("../../features/game/game-routing").then(m => m.gameRoutes),
    },
    {
        path: "**",
        redirectTo: "not-found",
    },
];

const mainRoutes: Routes = withLocalized(mainRoute);

export const routes: Routes = [
    ...mainRoutes,
    ...localizedGameProviderRoutes,
    ...localizedGameCategoryRoutes,
    {
        path: "game-providers/:providerName",
        loadChildren: () => import("../../features/game-hall/game-hall-routing").then(m => m.gameHallRoutes),
    },
    {
        path: "restore",
        loadChildren: () => import("../../features/home/home-routing").then(m => m.homeRoutes),
    },
    {
        path: "game-categories/:category-name",
        loadChildren: () => import("../../features/game-hall/game-hall-routing").then(m => m.gameHallRoutes),
    },
    {
        path: "promotions/:name",
        loadChildren: () => import("../../features/promotions/promo-item-routing").then(m => m.promoItemRouting),
    },

    ...sportRoutes,
    ...tournamentRoutes,
    ...missionsRoutes,
    ...lotteriesRoutes,
    ...achievementsRoutes,

    {
        path: "payment",
        loadChildren: () => import("../../modules/payment/payment-routing").then(m => m.paymentRoutes),
        data: {
            hideHeader: true,
            hideFooter: true,
            hideArguments: true,
            hideBottomMenu: true,
            hideBottomBanner: true,
        },
    },

    ...devRoutes,
];
